import React, { useEffect, useState, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useSelector } from 'react-redux';

import { components } from '../components';
import { hooks } from '../hooks';
import { items } from '../items';
import { Routes } from '../routes';
import { svg } from '../assets/svg';
import { RootState } from '../store';
import { setSelectedMenuCategory, setSelectedVegType, resetFilters } from '../store/slices/filterSlice';
import { DishType, MenuType } from '../types';

export const MenuList: React.FC = () => {
  const dispatch = hooks.useDispatch();
  const navigate = hooks.useNavigate();
  const location = hooks.useLocation();
  
  // Safe access to navigation state
  const initialMenuName: string = location.state?.menuName || "";

  // Data Fetching Hooks
  const { menuLoading, menu } = hooks.useGetMenu();
  const { dishesLoading, dishes } = hooks.useGetDishes();

  // Redux State
  const { selectedVegType, selectedCategoryType, selectedMenuCategory } =
    useSelector((state: RootState) => state.filterSlice);

  const [opacity, setOpacity] = useState<number>(0);

  // Initialize Category
  useEffect(() => {
    if (!menuLoading && menu.length > 0) {
      // If no category is selected, or we just came from a specific category on Home
      if (selectedMenuCategory === "") {
        const defaultCat = initialMenuName || menu[0].name;
        dispatch(setSelectedMenuCategory(defaultCat));
      }
    }
  }, [menuLoading, menu, dispatch, selectedMenuCategory, initialMenuName]);

  // Optimized Filter Logic
  const filteredDishes = useMemo(() => {
    return dishes.filter((dish: DishType) => {
      const matchCategory = dish.menu.some((m) =>
        m.name.toLowerCase() === selectedMenuCategory.toLowerCase()
      );
      
      const matchVeg = selectedVegType === "" || 
        dish.category.toLowerCase() === selectedVegType.toLowerCase();
        
      const matchChef = selectedCategoryType === "" || 
        dish.isChefSpecial === (selectedCategoryType === "chef-special");

      return matchCategory && matchVeg && matchChef;
    });
  }, [dishes, selectedMenuCategory, selectedVegType, selectedCategoryType]);

  hooks.useScrollToTop();
  hooks.useOpacity(setOpacity);
  hooks.useThemeColor('#F6F9F9', '#F6F9F9', dispatch);

  // --- Sub-Renders ---

  const renderSearchAndFilter = () => (
    <div style={styles.searchSection}>
      <button 
        style={styles.searchBar} 
        onClick={() => navigate(Routes.Search)}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 0 0 4px rgba(0, 176, 185, 0.08), 0 4px 12px rgba(0, 176, 185, 0.1)';
          e.currentTarget.style.borderColor = 'var(--main-turquoise)';
          e.currentTarget.style.backgroundColor = '#ffffff';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
          e.currentTarget.style.borderColor = '#e9ecef';
          e.currentTarget.style.backgroundColor = '#f8f9fa';
        }}
      >
        <div style={styles.searchIconWrapper}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: '#999' }}
          >
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
        </div>
        <span style={styles.searchPlaceholder}>
          Search in {selectedMenuCategory || 'menu'}...
        </span>
      </button>
      <button 
        style={styles.filterIconButton} 
        onClick={() => navigate(Routes.Filter)}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
          e.currentTarget.style.borderColor = 'var(--main-turquoise)';
          e.currentTarget.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
          e.currentTarget.style.borderColor = '#EDEDED';
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        <svg.FilterSvg />
      </button>
    </div>
  );

  const renderVegNonVegFilter = () => (
    <div style={styles.vegFilterSection}>
      <div style={styles.vegFilterContainer}>
        {["All", "Veg", "Non-Veg"].map((type) => {
          const isSelected = selectedVegType === type || (type === "All" && selectedVegType === "");
          return (
            <button
              key={type}
              style={{
                ...styles.vegFilterButton,
                backgroundColor: isSelected ? 'var(--main-turquoise)' : '#ffffff',
                borderColor: isSelected ? 'var(--main-turquoise)' : '#e9ecef',
                color: isSelected ? '#ffffff' : 'var(--main-color)',
                boxShadow: isSelected ? '0 2px 8px rgba(0, 176, 185, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
              }}
              onClick={() => dispatch(setSelectedVegType(type === "All" ? "" : type))}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              <span style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 12,
                fontWeight: 600,
              }}>
                {type}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderCategoryPills = () => (
    <div style={styles.categoriesWrapper}>
      <Swiper
        spaceBetween={10}
        slidesPerView={'auto'}
        slidesOffsetBefore={20}
        slidesOffsetAfter={20}
        grabCursor={true}
      >
        {menu.map((category: MenuType) => {
          const isActive = selectedMenuCategory === category.name;
          return (
            <SwiperSlide key={category.id} style={{ width: 'auto' }}>
              <div
                style={styles.pill(isActive)}
                onClick={() => dispatch(setSelectedMenuCategory(category.name))}
              >
                {category.name}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );

  const renderEmptyState = () => (
    <div style={styles.emptyContainer}>
      <div style={styles.emptyIcon}>🍳</div>
      <h3 style={styles.emptyTitle}>No dishes found</h3>
      <p style={styles.emptySubtitle}>Try adjusting your filters or exploring a different category.</p>
      <button 
        style={styles.resetBtn}
        onClick={() => {
          dispatch(resetFilters());
          if (menu && menu.length > 0) {
            dispatch(setSelectedMenuCategory(menu[0].name));
          }
        }}
      >
        Reset Filters
      </button>
    </div>
  );

  const isLoading = menuLoading || dishesLoading;

  return (
    <div id='screen' style={{ opacity, backgroundColor: '#F6F9F9', minHeight: '100vh' }}>
      <components.Header title="Menu" showGoBack showBasket />

      {/* STICKY HEADER AREA */}
      {!isLoading && menu.length > 0 && (
        <div style={styles.stickyHeader}>
          {renderSearchAndFilter()}
          {renderVegNonVegFilter()}
          {renderCategoryPills()}
        </div>
      )}

      {/* MAIN CONTENT */}
      {isLoading ? (
        <components.Loader />
      ) : (
        <main className="scrollable" style={{ paddingBottom: 40 }}>
          {filteredDishes.length > 0 ? (
            <ul style={styles.dishList}>
              {filteredDishes.map((dish, index) => (
                <items.MenuListItem
                  dish={dish}
                  key={dish.id}
                  isLast={index === filteredDishes.length - 1}
                />
              ))}
            </ul>
          ) : (
            renderEmptyState()
          )}
        </main>
      )}
    </div>
  );
};

// --- Professional Styles ---
const styles = {
  stickyHeader: {
    position: 'sticky' as const,
    top: 0,
    zIndex: 10,
    backgroundColor: '#F6F9F9',
    paddingBottom: 8,
    boxShadow: '0 8px 20px -12px rgba(0,0,0,0.08)',
  },
  searchSection: {
    padding: '20px',
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  },
  searchBar: {
    flex: 1,
    height: 56,
    backgroundColor: '#f8f9fa',
    borderRadius: 14,
    border: '1.5px solid #e9ecef',
    display: 'flex',
    alignItems: 'center',
    padding: '0 18px 0 50px',
    gap: 14,
    cursor: 'pointer',
    textAlign: 'left' as const,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    outline: 'none',
    position: 'relative' as const,
  },
  searchIconWrapper: {
    position: 'absolute' as const,
    left: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    pointerEvents: 'none' as const,
  },
  searchPlaceholder: {
    fontSize: 16,
    color: '#999',
    fontWeight: 400,
    fontFamily: 'DM Sans, sans-serif',
    flex: 1,
    textAlign: 'left' as const,
  },
  filterIconButton: {
    width: 52,
    height: 52,
    backgroundColor: '#FFF',
    borderRadius: 16,
    border: '2px solid #EDEDED',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    outline: 'none',
    flexShrink: 0,
  },
  vegFilterSection: {
    padding: '8px 20px',
    backgroundColor: '#ffffff',
    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
  },
  vegFilterContainer: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  vegFilterButton: {
    padding: '6px 14px',
    borderRadius: 10,
    border: '1.5px solid',
    cursor: 'pointer',
    fontFamily: 'DM Sans, sans-serif',
    fontSize: 12,
    fontWeight: 600,
    transition: 'all 0.2s ease',
    outline: 'none',
    flex: 1,
    maxWidth: 90,
  },
  categoriesWrapper: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  pill: (active: boolean): React.CSSProperties => ({
    padding: '10px 20px',
    borderRadius: '12px',
    backgroundColor: active ? 'var(--main-turquoise)' : '#FFF',
    color: active ? '#FFF' : '#5F6767',
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: active ? '1px solid var(--main-turquoise)' : '1px solid #EDEDED',
    boxShadow: active ? '0 4px 12px rgba(0, 176, 185, 0.2)' : '0 2px 4px rgba(0,0,0,0.02)',
    whiteSpace: 'nowrap',
  }),
  dishList: {
    padding: '10px 20px',
    margin: 0,
    listStyle: 'none',
  },
  emptyContainer: {
    padding: '80px 40px',
    textAlign: 'center' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
  },
  emptyIcon: { fontSize: 50, marginBottom: 16 },
  emptyTitle: { fontSize: 18, fontWeight: 700, color: '#2D3436', margin: '0 0 8px 0' },
  emptySubtitle: { fontSize: 14, color: '#95A5A6', marginBottom: 24, lineHeight: 1.5 },
  resetBtn: {
    backgroundColor: 'var(--main-turquoise)',
    color: '#FFF',
    border: 'none',
    padding: '12px 24px',
    borderRadius: 12,
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
  }
};